// ----------------------------------------------------------------------
// <copyright file="Izenda.LicenseInfo.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action } from 'mobx';
import { isNil, find } from 'lodash/fp';

import localizer from 'share/localization/Izenda.Localizer';
import LicenseInfoConstants from '../data/Izenda.LicenseInfoConstants';

export default class LicenseInfo {
  hasValidLicense = false;

  @observable licenseInfoDetail = {};
  @observable licenseStatus = {};
  // For hidden license token and key on UI
  @observable validatedLicense = false;
  @observable validatedLicenseToken = false;
  @observable isValidLicense = false;
  @observable disableLicense = true;
  @observable exceedLostConnectionAllowPeriod = false;
  @observable validationError = {};
  @observable licenseInValidMsg = '';
  @observable productVersion = '';
  @observable isConnectedToLicenseServer = true;

  @computed
  get isActiveLicense() {
    return this.integrationActiveLicense && this.isValidLicense;
  }

  @computed
  get integrationActiveLicense() {
    return (
      this.numberOfDayToExpire > 0 &&
      this.numberOfDayToValid === 0 &&
      !this.disableLicense &&
      !this.exceedLostConnectionAllowPeriod
    );
  }

  @computed
  get startDate() {
    return this.licenseInfoDetail.startDate || '';
  }

  @computed
  get endDate() {
    return this.licenseInfoDetail.endDate || '';
  }

  @computed
  get tokenKey() {
    return this.licenseInfoDetail.tokenKey || '';
  }

  @computed
  get licenseKey() {
    return this.licenseInfoDetail.licenseKey || '';
  }

  @computed
  get enableValidateButton() {
    return this.licenseOnlineMode ? !!this.licenseKey : !!(this.tokenKey && this.licenseKey);
  }

  @computed
  get licenseOnlineMode() {
    return this.licenseInfoDetail.licenseOnlineMode;
  }

  @computed
  get licenseEndDate() {
    return this.licenseInfoDetail.licenseEndDate || '';
  }

  @computed
  get previousStartDate() {
    return this.licenseInfoDetail.previousStartDate || '';
  }

  @computed
  get previousEndDate() {
    return this.licenseInfoDetail.previousEndDate || '';
  }

  @computed
  get modules() {
    return this.licenseInfoDetail.modules || [];
  }

  @computed
  get previousModules() {
    return this.licenseInfoDetail.previousModules || [];
  }

  @computed
  get hasMapModules() {
    return !!find(function (module) {
      return module.name === LicenseInfoConstants.MAP_MODULES_NAME;
    }, this.modules);
  }

  @computed
  get numberOfDayToValid() {
    return (
      (this.licenseInfoDetail && this.licenseInfoDetail.numberOfDayToValid) ||
      (this.licenseStatus && this.licenseStatus.numberOfDayToValid)
    );
  }

  @computed
  get meetExprireWarningPeriod() {
    return (this.licenseStatus && this.licenseStatus.meetExprireWarningPeriod) || false;
  }

  @computed
  get numberOfDayToExpire() {
    return this.licenseStatus && this.licenseStatus.numberOfDayToExpire;
  }

  @computed
  get trialLicense() {
    return (this.licenseStatus && this.licenseStatus.trialLicense) || false;
  }

  @computed
  get isOnlineLicenseValidInLostConnectionAllowPeriod() {
    return (
      this.licenseOnlineMode &&
      this.isValidLicense &&
      !this.isConnectedToLicenseServer &&
      !this.exceedLostConnectionAllowPeriod
    );
  }

  /**
   * [readLicenseStatus description]
   * @param  {object} status [receive from API]
   */
  @action
  readLicenseStatus(status) {
    status = status || {};
    this.licenseStatus = status;
    this.disableLicense = status.disabled;
    this.exceedLostConnectionAllowPeriod = status.exceedLostConnectionAllowPeriod;
    this.isConnectedToLicenseServer = status.connected;
    status.exceedLostConnectionAllowPeriod && (this.isValidLicense = false);
  }

  /**
   * read validate response from api
   * @param  {object} response [from API]
   */
  @action
  readValidateLicense(response) {
    this.validatedLicense = true;
    this.validatedLicenseToken = true;
    this.isValidLicense = response.success;
    if (response.success) {
      this.licenseInfoDetail = response;
      this.hasValidLicense = true;
    }
  }

  /**
   * read license information response from api
   * @param  {object} response from api
   */
  @action
  readLicenseInforation(response) {
    // Set default license mode = online
    if (!response.success) {
      response.licenseOnlineMode = true;
    }
    this.hasValidLicense = this.isValidLicense = this.validatedLicense = this.validatedLicenseToken =
      isNil(response.success)
        ? !!response.licenseKey
        : response.success;
    this.disableLicense = !response.licenseEnable;
    this.validationError = this.disableLicense ? [LicenseInfoConstants.MESSAGE.MG10] : [];
    this.licenseInValidMsg = '';
    this.licenseInfoDetail = response;
    this.productVersion = response.productVersion
      ? String.format(localizer.getText('COMMON_CRITERIA_VERSION') + ' {0}', response.productVersion)
      : '';
  }

  /**
   * Reset license information
   */
  @action
  resetLicenseInfo() {
    this.validatedLicense = false;
    this.validatedLicenseToken = false;
    this.isValidLicense = false;
    this.hasValidLicense = false;
    this.exceedLostConnectionAllowPeriod = false;
    this.licenseInfoDetail = {};
    this.licenseStatus = {};
  }

  /**
   * Update license info by key
   * This license info will be set as editable and invalid license
   * @param {type} key
   * @param {type} value
   */
  @action
  updateLicenseInfoByKey(key, value) {
    if (key === 'tokenKey') {
      this.validatedLicenseToken = false;
    } else if (key === 'licenseKey') {
      this.validatedLicense = false;
    } else {
      this.validatedLicenseToken = false;
      this.validatedLicense = false;
    }

    if (this.isValidLicense) {
      this.isValidLicense = false;
    }
    this.licenseInfoDetail[key] = value;
  }
}

// Default export - instance
export const licenseInfoInstance = new LicenseInfo();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.LicenseInfo.js